import { useMsal } from '@azure/msal-react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { User, Building2, Globe, Wallet } from 'lucide-react'

import LanguageSwitcher from '../components/LanguageSwitcher'
import { apiClient } from '../services/api'

interface CostCenterPermission {
  id: string
  cost_center_id: string
  cost_center_code: string
  cost_center_name: string
  valid_from: string
  valid_to: string | null
  is_active: boolean
}

export default function SettingsPage() {
  const { t } = useTranslation('admin')
  const { t: tc } = useTranslation('common')
  const { accounts } = useMsal()
  
  const account = accounts[0]

  // Fetch user's cost center permissions
  const { data: permissions } = useQuery<CostCenterPermission[]>({
    queryKey: ['my-cost-center-permissions'],
    queryFn: () => apiClient.get('/cost-centers/my/permissions'),
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Profile */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center mb-4">
            <User className="w-5 h-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t('settings.profile')}
            </h2>
          </div>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm text-gray-500">{tc('common.name')}</dt>
              <dd className="font-medium">{account?.name || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Email</dt>
              <dd className="font-medium">{account?.username || '-'}</dd>
            </div>
          </dl>
        </div>

        {/* Organization */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center mb-4">
            <Building2 className="w-5 h-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t('settings.organization')}
            </h2>
          </div>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm text-gray-500">Tenant ID</dt>
              <dd className="font-medium font-mono text-sm">
                {account?.tenantId || '-'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Language */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center mb-4">
            <Globe className="w-5 h-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t('settings.language')}
            </h2>
          </div>
          <div className="mt-2">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Cost Center Permissions */}
        <div className="bg-white rounded-xl shadow-sm border p-6 lg:col-span-2">
          <div className="flex items-center mb-4">
            <Wallet className="w-5 h-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t('settings.costCenterPermissions')}
            </h2>
          </div>
          {permissions && permissions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('settings.costCenterCode')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('settings.costCenterName')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('settings.validFrom')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('settings.validTo')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {permissions.map((perm) => (
                    <tr key={perm.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-mono font-medium">
                        {perm.cost_center_code}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {perm.cost_center_name}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {new Date(perm.valid_from).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {perm.valid_to 
                          ? new Date(perm.valid_to).toLocaleDateString()
                          : <span className="text-green-600">{t('settings.validIndefinitely')}</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 italic">{t('settings.noCostCenterPermissions')}</p>
          )}
        </div>
      </div>
    </div>
  )
}
